import { QueryClient, useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import type {
  AuditEntry,
  Client,
  ClientSummary,
  Contract,
  Dashboard,
  EquipmentType,
  Intervention,
  InterventionDetail,
  InterventionType,
  MaintenanceOverview,
  OnCallWeek,
  Onboarding,
  Page,
  Part,
  Photo,
  Todo,
  TripUsage,
  User
} from "@techplanner/shared/core";
import { api, ApiError } from "./api";

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 1000 * 60 * 60 * 24 * 7,
        retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
        refetchOnWindowFocus: true,
        networkMode: "offlineFirst"
      },
      mutations: { networkMode: "always", retry: false }
    }
  });
}

/* Clés de cache : un seul endroit pour les invalider correctement. */
export const keys = {
  session: ["session"] as const,
  users: (all = false) => ["users", { all }] as const,
  dashboard: ["dashboard"] as const,
  maintenance: (horizon: number) => ["maintenance", horizon] as const,
  interventions: (params: Record<string, string>) => ["interventions", params] as const,
  intervention: (id: number) => ["intervention", id] as const,
  clients: (archived = false) => ["clients", { archived }] as const,
  client: (id: number) => ["client", id] as const,
  clientSub: (id: number, sub: string) => ["client", id, sub] as const,
  contracts: (status?: string) => ["contracts", status ?? "all"] as const,
  contract: (id: number) => ["contract", id] as const,
  todos: (status: string) => ["todos", status] as const,
  onCall: (from: string, weeks: number) => ["on-call", from, weeks] as const,
  audit: ["audit"] as const,
  onboarding: ["onboarding"] as const,
  interventionTypes: (all = false) => ["intervention-types", { all }] as const,
  equipmentTypes: (all?: boolean) => (all === undefined ? (["equipment-types"] as const) : (["equipment-types", { all }] as const))
};

const qs = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") search.set(k, String(v));
  const s = search.toString();
  return s ? `?${s}` : "";
};

export const useUsers = (all = false) =>
  useQuery({ queryKey: keys.users(all), queryFn: () => api<User[]>(`/users${all ? "?all=1" : ""}`), staleTime: 5 * 60_000 });

/** Types d'intervention de la société (les admins voient aussi les types désactivés avec all). */
export const useInterventionTypes = (all = false) =>
  useQuery({
    queryKey: keys.interventionTypes(all),
    queryFn: () => api<InterventionType[]>(`/intervention-types${all ? "?all=1" : ""}`),
    staleTime: 5 * 60_000
  });

/** Catalogue « Mes équipements » (les admins voient aussi les types désactivés avec all). */
export const useEquipmentTypes = (all = false) =>
  useQuery({
    queryKey: keys.equipmentTypes(all),
    queryFn: () => api<EquipmentType[]>(`/equipment-types${all ? "?all=1" : ""}`),
    staleTime: 5 * 60_000
  });

export const useDashboard = () => useQuery({ queryKey: keys.dashboard, queryFn: () => api<Dashboard>("/dashboard") });

export const useMaintenance = (horizon: number) =>
  useQuery({ queryKey: keys.maintenance(horizon), queryFn: () => api<MaintenanceOverview>(`/maintenance?horizon=${horizon}`) });

export const useInterventions = (params: Record<string, string>, enabled = true) =>
  useQuery({
    queryKey: keys.interventions(params),
    queryFn: () => api<Intervention[]>(`/interventions${qs(params)}`),
    enabled,
    placeholderData: (previous) => previous
  });

export const useIntervention = (id: number) =>
  useQuery({ queryKey: keys.intervention(id), queryFn: () => api<InterventionDetail>(`/interventions/${id}`), enabled: id > 0 });

export const useClients = (archived = false) =>
  useQuery({ queryKey: keys.clients(archived), queryFn: () => api<ClientSummary[]>(`/clients${archived ? "?archived=1" : ""}`) });

export const useClient = (id: number) =>
  useQuery({ queryKey: keys.client(id), queryFn: () => api<Client>(`/clients/${id}`), enabled: id > 0 });

export const useClientInterventions = (id: number) =>
  useQuery({ queryKey: keys.clientSub(id, "interventions"), queryFn: () => api<Intervention[]>(`/clients/${id}/interventions`) });
export const useClientContracts = (id: number) =>
  useQuery({ queryKey: keys.clientSub(id, "contracts"), queryFn: () => api<Contract[]>(`/clients/${id}/contracts`) });
export const useClientTrips = (id: number) =>
  useQuery({ queryKey: keys.clientSub(id, "trips"), queryFn: () => api<TripUsage[]>(`/clients/${id}/trips`) });
export const useClientPhotos = (id: number) =>
  useQuery({ queryKey: keys.clientSub(id, "photos"), queryFn: () => api<Photo[]>(`/clients/${id}/photos`) });
export const useClientParts = (id: number) =>
  useQuery({ queryKey: keys.clientSub(id, "parts"), queryFn: () => api<Part[]>(`/clients/${id}/parts`) });
export const useClientTodos = (id: number) =>
  useQuery({ queryKey: keys.clientSub(id, "todos"), queryFn: () => api<Todo[]>(`/clients/${id}/todos`) });

export const useContracts = (status?: "active" | "ended") =>
  useQuery({ queryKey: keys.contracts(status), queryFn: () => api<Contract[]>(`/contracts${qs({ status })}`) });

export interface ContractDetail {
  contract: Contract;
  occurrences: Intervention[];
  trips: TripUsage[];
}
export const useContract = (id: number) =>
  useQuery({ queryKey: keys.contract(id), queryFn: () => api<ContractDetail>(`/contracts/${id}`), enabled: id > 0 });

export const useTodos = (status: "open" | "done") =>
  useQuery({ queryKey: keys.todos(status), queryFn: () => api<Todo[]>(`/todos${status === "done" ? "?status=done" : ""}`) });

export const useOnCall = (from: string, weeks: number) =>
  useQuery({ queryKey: keys.onCall(from, weeks), queryFn: () => api<OnCallWeek[]>(`/on-call?from=${from}&weeks=${weeks}`) });

export const useAudit = () => useQuery({ queryKey: keys.audit, queryFn: () => api<Page<AuditEntry>>("/audit") });

/** Tutoriel de démarrage (admin) : toujours revérifié, il se coche au fil des actions. */
export const useOnboarding = (enabled: boolean) =>
  useQuery({ queryKey: keys.onboarding, queryFn: () => api<Onboarding>("/onboarding"), enabled, staleTime: 0 });

/** Après une écriture : rafraîchit tout ce qui peut afficher des interventions. */
export function useInvalidate() {
  const qc = useQueryClient();
  return (...extra: QueryKey[]) => {
    const prefixes: QueryKey[] = [["interventions"], ["dashboard"], ["maintenance"], ["clients"], ["client"], ["contracts"], ["contract"], ["onboarding"], ...extra];
    return Promise.all(prefixes.map((queryKey) => qc.invalidateQueries({ queryKey })));
  };
}

export { useMutation, useQueryClient };
